import clsx from "clsx";
import { FC, useEffect, useMemo, useRef, useTransition } from "react";
import { FormProvider } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { RASTER_LANDINGS } from "@ryde/assets-landings";
import {
  DropdownTemplate,
  MessagesDisplayContainer,
  PhoneTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import { normalizePhoneNumber } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  ClalChooseStationStepFormFieldsEnum,
  LANDING_RYDE_SUPPORT_PHONE,
  landingColors,
  landingDropdownStationListAtom,
  landingInitDataAtom,
  landingMessagesAtom,
  useLandingProfile,
} from "@ryde/utils-landings";

import { IMarkerLocation, LandingAddressMap } from "../../layout/landing-map";
import { LandingAddressTemplate } from "../../layout/landing-templates";
import { ContinueButton } from "../../widgets";
import { ClalWorkerType } from "./parts";
import { useStepChooseStationClal } from "./use-step-choose-station.clal";

export const StepChooseStationClal: FC = () => {
  const {
    methods,
    handleChooseStation,
    handleHomeAddressChange,
    isContinueCanBeApply,
    unitsDropdownList,
    workerTypeDropdownList,
  } = useStepChooseStationClal();
  const { watch } = methods;

  const landingMessages = useRecoilValue(landingMessagesAtom);
  const stationsDropdownList = useRecoilValue(landingDropdownStationListAtom);
  const isHomeAddressTouchedRef = useRef<boolean>(false);
  const { fetchAndSetProfile } = useLandingProfile();
  const [, startHomeAddressChangeTransition] = useTransition();
  const [, startFetchProfileTransition] = useTransition();
  const isApiLoading = useRecoilValue(apiDataFetchingLandingAtom);
  const { t } = useTranslation();
  const { supportCenterPhone } = useRecoilValue(landingInitDataAtom) ?? {};

  const {
    latitude: lat,
    longitude: lng,
    fullAddress,
  } = watch(ClalChooseStationStepFormFieldsEnum.HomeAddress);
  const sourceStation2IdWatchField = watch(
    ClalChooseStationStepFormFieldsEnum.SourceStation2Id,
  );
  const workerType = watch(ClalChooseStationStepFormFieldsEnum.WorkerType);

  const markers = useMemo(() => {
    if (!lat || !lng) {
      return [];
    }

    const locations: IMarkerLocation[] = [
      {
        coordinate: { lat, lng },
        isCenter: true,
        popupText: fullAddress,
        icon: <SVG_MUI.HomePinSVG color={landingColors.purpleHaze} />,
      },
    ];

    const { locationInfo: pickUpLocation, label } =
      stationsDropdownList?.find(
        item => item.value === sourceStation2IdWatchField,
      ) ?? {};

    if (pickUpLocation?.latitude && pickUpLocation?.longitude) {
      locations.push({
        coordinate: {
          lat: pickUpLocation.latitude,
          lng: pickUpLocation.longitude,
        },
        isCenter: false,
        popupText: label ?? pickUpLocation.fullAddress,
      });
    }

    return locations;
  }, [fullAddress, lat, lng, sourceStation2IdWatchField, stationsDropdownList]);

  useEffect(() => {
    if ((lat || lng || fullAddress) && !isHomeAddressTouchedRef.current) {
      isHomeAddressTouchedRef.current = true;
    }

    if (isHomeAddressTouchedRef.current) {
      startHomeAddressChangeTransition(() => {
        handleHomeAddressChange();
      });
    }
  }, [lat, lng, fullAddress, handleHomeAddressChange]);

  useEffect(() => {
    startFetchProfileTransition(() => {
      fetchAndSetProfile();
    });
  }, [fetchAndSetProfile]);

  return (
    <FormProvider {...methods}>
      <div className="landing-top-illustration">
        <img src={RASTER_LANDINGS.CarFestImagePNG} alt="Car fest" />
      </div>
      <div className="landing-title landing-title_choose-station">
        {t("landing.chooseStation.titleClal")}
      </div>
      <div className="landing-sub-title">
        {t("landing.chooseStation.subTitleClal")}
      </div>

      <div className="landing-form-field">
        <label htmlFor={ClalChooseStationStepFormFieldsEnum.WorkerId}>
          {t("landing.formLabel.workerId")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={ClalChooseStationStepFormFieldsEnum.WorkerId}
          name={ClalChooseStationStepFormFieldsEnum.WorkerId}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={ClalChooseStationStepFormFieldsEnum.FirstName}>
          {t("landing.formLabel.firstName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={ClalChooseStationStepFormFieldsEnum.FirstName}
          name={ClalChooseStationStepFormFieldsEnum.FirstName}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={ClalChooseStationStepFormFieldsEnum.LastName}>
          {t("landing.formLabel.lastName")}*
        </label>
        <TextboxTemplate
          uniqueAttrId={ClalChooseStationStepFormFieldsEnum.LastName}
          name={ClalChooseStationStepFormFieldsEnum.LastName}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={ClalChooseStationStepFormFieldsEnum.Phone}>
          {t("landing.formLabel.phone")}*
        </label>
        <PhoneTemplate
          uniqueAttrId={ClalChooseStationStepFormFieldsEnum.Phone}
          name={ClalChooseStationStepFormFieldsEnum.Phone}
          sx={{ size: "small" }}
          formAttributes={{ disablePhoneCallable: true }}
        />
      </div>

      <div className="landing-form-field">
        <label htmlFor={ClalChooseStationStepFormFieldsEnum.HomeAddress}>
          {t("landing.formLabel.homeAddress")}*
        </label>
        <LandingAddressTemplate
          uniqueAttrId={ClalChooseStationStepFormFieldsEnum.HomeAddress}
          name={ClalChooseStationStepFormFieldsEnum.HomeAddress}
          formAttributes={{
            isCoordinate: true,
          }}
        />
      </div>

      <div
        className={clsx("landing-form-field", {
          hidden: !stationsDropdownList?.length || !fullAddress,
        })}
      >
        <label htmlFor={ClalChooseStationStepFormFieldsEnum.SourceStation2Id}>
          {t("landing.formLabel.sourceStation2Id")}*
        </label>
        <DropdownTemplate
          uniqueAttrId={ClalChooseStationStepFormFieldsEnum.SourceStation2Id}
          name={ClalChooseStationStepFormFieldsEnum.SourceStation2Id}
          size="medium"
          formAttributes={{
            selectItems: stationsDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>

      <div className="landing-form-field">
        <div className="landing-address-map-field">
          <LandingAddressMap locations={markers} readonly />
        </div>
      </div>

      <div className="landing-form-field">
        <label htmlFor={ClalChooseStationStepFormFieldsEnum.WorkerType}>
          {t("landing.formLabel.workerType")}
        </label>
        <DropdownTemplate
          uniqueAttrId={ClalChooseStationStepFormFieldsEnum.WorkerType}
          name={ClalChooseStationStepFormFieldsEnum.WorkerType}
          size="medium"
          formAttributes={{
            selectItems: workerTypeDropdownList,
            skipDropdownItemsTranslation: true,
          }}
        />
      </div>
      {workerType === ClalWorkerType.Contractor && (
        <div className="landing-form-field">
          <label htmlFor={ClalChooseStationStepFormFieldsEnum.Unit}>
            {t("landing.formLabel.unitId")}*
          </label>
          <DropdownTemplate
            uniqueAttrId={ClalChooseStationStepFormFieldsEnum.Unit}
            name={ClalChooseStationStepFormFieldsEnum.Unit}
            size="medium"
            formAttributes={{
              selectItems: unitsDropdownList,
              skipDropdownItemsTranslation: true,
            }}
          />
        </div>
      )}

      <MessagesDisplayContainer
        serverMessages={landingMessages?.messages}
        type={landingMessages?.type}
      />

      <ContinueButton
        disabled={!isContinueCanBeApply}
        onClick={handleChooseStation}
        loading={isApiLoading}
      />

      <div className="landing-container-footer">
        <div className="landing-container-footer-logo">
          <img
            className="landing-logo__img"
            src={RASTER_LANDINGS.RideLogoPNG}
            alt="Ryde logo"
            width="120"
            height="51"
          />
        </div>

        <span className="landing-customer-support custom-label">
          <Trans
            i18nKey="landing.helpDisclaimer"
            values={{
              phoneNumber: normalizePhoneNumber(LANDING_RYDE_SUPPORT_PHONE),
            }}
            components={{
              secure_link: (
                <a
                  aria-label="call center phone number"
                  href={`tel:${LANDING_RYDE_SUPPORT_PHONE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                />
              ),
              br: <br />,
            }}
          />
        </span>
      </div>
    </FormProvider>
  );
};
